import { useCurrentCompany } from '@bonusly/who-am-i';

import { useGithubStatus } from '../../../queries/github';
import { useGranolaStatus } from '../../../queries/granola';
import { useLinearStatus } from '../../../queries/linear';
import { useMsTeamsStatus } from '../../../queries/ms-teams';
import { useSlackStatus } from '../../../queries/slack';

export interface BizyConnectorCounts {
	/** Number of enabled connectors that are currently connected. */
	connected: number;
	/** Number of connectors enabled for the company. */
	total: number;
}

/**
 * Connected / total connector counts for the People Browser footer (BIZY-636).
 *
 * `total` is the set of connectors enabled for the company; `connected` counts
 * those whose status endpoint reports `connected`. Each per-connector status
 * query is gated on the company flag so we don't fetch statuses for connectors
 * that aren't available.
 */
export function useBizyConnectorCounts(): BizyConnectorCounts {
	const company = useCurrentCompany();

	const hasGranola = Boolean(company?.bizy_connectors_granola);
	const hasLinear = Boolean(company?.bizy_connectors_linear);
	const hasGithub = Boolean(company?.bizy_connectors_github);
	const hasSlack = Boolean(company?.bizy_connectors_slack);
	const hasMsTeams = Boolean(company?.bizy_connectors_ms_teams);

	const granola = useGranolaStatus(hasGranola);
	const linear = useLinearStatus(hasLinear);
	const github = useGithubStatus(hasGithub);
	const slack = useSlackStatus(hasSlack);
	const msTeams = useMsTeamsStatus(hasMsTeams);

	const total = [hasGranola, hasLinear, hasGithub, hasSlack, hasMsTeams].filter(Boolean).length;

	const connected = [
		hasGranola && granola.data?.status === 'connected',
		hasLinear && linear.data?.status === 'connected',
		hasGithub && github.data?.status === 'connected',
		hasSlack && slack.data?.status === 'connected',
		hasMsTeams && msTeams.data?.status === 'connected',
	].filter(Boolean).length;

	return { connected, total };
}
